import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MetaTags from '@/components/SEO/MetaTags';
import BrandLogo from '@/components/brand/BrandLogo';
import { useAuth } from '@/contexts/AuthContext';
import { startVoiceSession, sendVoiceTurn } from '@/voice/byoncoVoice';
import ProPaywallModal from '@/components/ProPaywallModal';
import { getSubscriptionState, isAdmin } from '@/utils/subscription';
import TalkRing from './TalkRing';
import { canShowVoiceNote, talkGate } from './talkAccess';
import { currentTalkNote, previousTalkNotes, pushTalkNote } from './talkNotes';
import { loadTalkSession, saveTalkSession, talkLanguage, talkUserKey, TALK_LANGS } from './talkSession';
import { audioMimeOf, blobFromBase64, speakWithBrowser, unlockSpeech, primedAudio } from './talkSpeak';
import { wwwUrl, goProduct, talkUrl, memberPath } from '@/config/hosts';
import './TalkPage.css';

const LANGS = TALK_LANGS;

const LISTEN_CAP = 20;
const VOICE_CREDITS = 6;

function audioToB64(buf) {
  const bytes = new Uint8Array(buf);
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    s += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  return btoa(s);
}

function statusCopy(phase, lang) {
  if (phase === 'listening') return lang === 'es' ? 'Escuchando' : 'Listening';
  if (phase === 'thinking') return lang === 'es' ? 'Pensando' : 'Thinking';
  if (phase === 'speaking') return lang === 'es' ? 'Hablando' : 'Speaking';
  return lang === 'es' ? 'Toca para hablar' : 'Tap to talk';
}

function NoteCard({ kicker, text, testId, tone = 'past' }) {
  return (
    <article className={`talk-card talk-card-${tone}`} data-testid={testId}>
      <p className="talk-card-kicker">{kicker}</p>
      <p className="talk-card-body">{text}</p>
    </article>
  );
}

export default function TalkPage() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [lang, setLang] = useState('en');
  const [phase, setPhase] = useState('idle');
  const [energy, setEnergy] = useState(0.12);
  const [note, setNote] = useState('');
  const [notes, setNotes] = useState([]);
  const [alert, setAlert] = useState('');
  const [paywall, setPaywall] = useState(false);
  const [isPro, setIsPro] = useState(false);
  const [entitlementReady, setEntitlementReady] = useState(false);
  const sessionRef = useRef(null);
  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const audioRef = useRef(null);
  const objectUrlRef = useRef(null);
  const cancelSpeakRef = useRef(null);
  const closingRef = useRef(false);
  const spokenRef = useRef('');
  const notesRef = useRef([]);
  const userKey = talkUserKey(user);
  const [firstName, setFirstName] = useState('');
  const gate = talkGate({ isAuthenticated, isPro });
  const earlier = previousTalkNotes(notes);
  const liveNote = note || currentTalkNote(notes);
  const showNow = Boolean(liveNote);

  useEffect(() => {
    document.documentElement.classList.add('talk-lock');
    document.body.classList.add('talk-lock');
    return () => {
      document.documentElement.classList.remove('talk-lock');
      document.body.classList.remove('talk-lock');
      stopListen(false);
      stopPlayback();
    };
  }, []);

  useEffect(() => {
    try { window.speechSynthesis?.getVoices(); } catch (_) { /* ignore */ }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isAuthenticated) {
        if (!cancelled) {
          setIsPro(false);
          setEntitlementReady(true);
        }
        return;
      }
      if (isAdmin(user)) {
        if (!cancelled) {
          setIsPro(true);
          setEntitlementReady(true);
        }
        return;
      }
      const state = await getSubscriptionState(user).catch(() => ({ isActive: false }));
      if (cancelled) return;
      const active = !!state.isActive;
      setIsPro(active);
      setEntitlementReady(true);
      if (!active) setPaywall(true);
    })();
    return () => { cancelled = true; };
  }, [user, isAuthenticated]);

  useEffect(() => {
    if (!userKey) return;
    const saved = loadTalkSession(userKey);
    if (!saved?.sessionId) return;
    sessionRef.current = saved.sessionId;
    if (saved.language) setLang(talkLanguage(saved.language));
    const list = saved.notes?.length
      ? saved.notes
      : (saved.lastNote ? pushTalkNote([], saved.lastNote) : []);
    keepNotes(list);
    const current = currentTalkNote(list);
    if (current) setNote(current);
    if (saved.firstName) setFirstName(saved.firstName);
  }, [userKey]);

  useEffect(() => {
    if (!entitlementReady || gate !== 'ready' || !userKey) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const started = await startVoiceSession(lang);
        if (cancelled) return;
        rememberSession(started, {
          lastNote: currentTalkNote(notesRef.current),
          firstName: loadTalkSession(userKey)?.firstName,
          notes: notesRef.current,
        });
      } catch (_) {
        /* first tap will retry */
      }
    })();
    return () => { cancelled = true; };
  }, [entitlementReady, gate, userKey, lang]);

  function keepNotes(next) {
    notesRef.current = next;
    setNotes(next);
  }

  function persistTalk(extra = {}) {
    if (!userKey || !sessionRef.current) return;
    const list = extra.notes || notesRef.current;
    const saved = loadTalkSession(userKey);
    saveTalkSession(userKey, {
      sessionId: sessionRef.current,
      conversationId: extra.conversationId || saved?.conversationId || '',
      language: talkLanguage(extra.language || saved?.language || lang),
      lastNote: extra.lastNote || currentTalkNote(list),
      firstName: extra.firstName || firstName || saved?.firstName || '',
      resumed: extra.resumed !== undefined ? extra.resumed : true,
      notes: list,
    });
  }

  function stopPlayback() {
    if (cancelSpeakRef.current) {
      try { cancelSpeakRef.current(); } catch (_) { /* ignore */ }
      cancelSpeakRef.current = null;
    }
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (_) { /* ignore */ }
      audioRef.current = null;
    }
    if (objectUrlRef.current) {
      try { URL.revokeObjectURL(objectUrlRef.current); } catch (_) { /* ignore */ }
      objectUrlRef.current = null;
    }
  }

  function stopListen(resetPhase = true) {
    const rec = recRef.current;
    recRef.current = null;
    if (rec) {
      clearInterval(rec._tick);
      clearTimeout(rec._cap);
      try { rec.stop(); } catch (_) { /* ignore */ }
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (resetPhase && phase === 'listening') setPhase('idle');
  }

  function revealNote(text) {
    const spoken = String(text || spokenRef.current || '').trim();
    spokenRef.current = spoken;
    const next = pushTalkNote(notesRef.current, spoken);
    keepNotes(next);
    setNote(spoken);
    setPhase('noted');
    setEnergy(0.12);
    persistTalk({ lastNote: spoken, notes: next, resumed: true });
  }

  function openGate() {
    if (!entitlementReady) return false;
    if (gate === 'signin') {
      goProduct(wwwUrl('/authentication?redirect=%2Ftalk'), navigate);
      return false;
    }
    if (gate === 'paywall') {
      setPaywall(true);
      return false;
    }
    return true;
  }

  function rememberSession(started, extra = {}) {
    if (!started?.session_id) return null;
    sessionRef.current = started.session_id;
    const lastNote = started.last_note || extra.lastNote || currentTalkNote(notesRef.current) || '';
    const name = started.first_name || extra.firstName || '';
    if (name) setFirstName(name);
    let list = extra.notes || notesRef.current || [];
    if (!list.length && lastNote) list = pushTalkNote([], lastNote);
    keepNotes(list);
    const current = currentTalkNote(list);
    if (current) setNote(current);
    persistTalk({
      conversationId: started.conversation_id,
      language: talkLanguage(started.language || lang),
      lastNote: current || lastNote,
      firstName: name,
      resumed: !!started.resumed,
      notes: list,
    });
    return started.session_id;
  }

  async function ensureSession() {
    if (sessionRef.current) return sessionRef.current;
    const started = await startVoiceSession(lang);
    return rememberSession(started);
  }

  async function startListen() {
    closingRef.current = false;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    streamRef.current = stream;
    chunksRef.current = [];
    const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : 'audio/webm';
    const rec = new MediaRecorder(stream, { mimeType: mime });
    rec.ondataavailable = (e) => { if (e.data?.size) chunksRef.current.push(e.data); };
    rec.start(200);
    recRef.current = rec;
    const started = Date.now();
    rec._tick = setInterval(() => {
      const left = Math.max(0, LISTEN_CAP - Math.floor((Date.now() - started) / 1000));
      setEnergy(0.45 + ((LISTEN_CAP - left) % 4) * 0.08);
    }, 350);
    rec._cap = setTimeout(() => finishListen(), LISTEN_CAP * 1000);
    setPhase('listening');
    spokenRef.current = '';
    setAlert('');
  }

  async function finishListen() {
    if (closingRef.current) return;
    const rec = recRef.current;
    recRef.current = null;
    if (!rec) return;
    closingRef.current = true;
    clearInterval(rec._tick);
    clearTimeout(rec._cap);
    setPhase('thinking');
    const blob = await new Promise((resolve) => {
      rec.onstop = () => resolve(new Blob(chunksRef.current, { type: rec.mimeType || 'audio/webm' }));
      try { rec.stop(); } catch (_) { resolve(new Blob()); }
    });
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (!blob.size) {
      closingRef.current = false;
      setPhase('idle');
      setAlert('I did not catch that. Tap and try again.');
      return;
    }
    try {
      const sessionId = await ensureSession();
      const result = await sendVoiceTurn({
        sessionId,
        audioBase64: audioToB64(await blob.arrayBuffer()),
        mime: blob.type || 'audio/webm',
        includeAudio: lang !== 'en',
        language: talkLanguage(lang),
      });
      if (result.error === 'insufficient_credits') {
        closingRef.current = false;
        setPaywall(true);
        setPhase('idle');
        return;
      }
      if (!result.ok && result.error) {
        closingRef.current = false;
        setPhase('idle');
        setAlert(result.error === 'empty_input'
          ? 'I did not catch that. Tap and try again.'
          : 'I could not finish that just now. Tap and try again.');
        return;
      }
      const text = String(result.reply || '').trim();
      spokenRef.current = text;
      persistTalk({
        language: talkLanguage(result.language || lang),
        lastNote: text || currentTalkNote(notesRef.current),
        firstName,
        resumed: true,
      });
      if (result.safety === 'emergency') {
        setAlert(text);
      }
      closingRef.current = false;
      if (text) await playAnswer(result.audio_base64, text, audioMimeOf(result), talkLanguage(result.language || lang));
      else setPhase('idle');
    } catch (err) {
      closingRef.current = false;
      setPhase('idle');
      if (err.status === 401) {
        goProduct(wwwUrl('/authentication?redirect=%2Ftalk'), navigate);
        return;
      }
      if (err.status === 402) {
        setIsPro(false);
        setPaywall(true);
        return;
      }
      if (err.status === 404) {
        sessionRef.current = null;
      }
      setAlert('I could not finish that just now. Tap and try again.');
    }
  }

  async function playAnswer(b64, text, mime, speakLang) {
    stopPlayback();
    spokenRef.current = text;
    setPhase('speaking');
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      if (audioRef.current) audioRef.current = null;
      revealNote(text);
    };
    const blob = b64 ? blobFromBase64(b64, mime) : null;
    if (blob && blob.size > 40) {
      const url = URL.createObjectURL(blob);
      objectUrlRef.current = url;
      const audio = primedAudio() || new Audio();
      audioRef.current = audio;
      audio.src = url;
      audio.ontimeupdate = () => {
        setEnergy(0.4 + Math.abs(Math.sin(audio.currentTime * 3)) * 0.35);
      };
      audio.onended = finish;
      try {
        await audio.play();
        return;
      } catch (_) {
        audioRef.current = null;
        if (objectUrlRef.current) {
          try { URL.revokeObjectURL(objectUrlRef.current); } catch (__) { /* ignore */ }
          objectUrlRef.current = null;
        }
      }
    }
    setEnergy(0.55);
    cancelSpeakRef.current = speakWithBrowser(text, speakLang, {
      onBoundary: () => setEnergy(0.45 + Math.random() * 0.3),
      onEnd: finish,
    });
  }

  async function onOrb() {
    if (phase === 'thinking') return;
    if (phase === 'speaking') {
      stopPlayback();
      revealNote(spokenRef.current);
      return;
    }
    if (!openGate()) return;
    unlockSpeech();
    if (phase === 'listening') {
      await finishListen();
      return;
    }
    try {
      await startListen();
    } catch (_) {
      setAlert('Allow the microphone, then tap again.');
    }
  }

  const hint = !isAuthenticated
    ? 'Sign in, open a plan, then tap and ask out loud.'
    : !isPro
      ? 'Voice is on a plan. Open a plan to talk with Vani.'
      : phase === 'listening'
        ? 'Tap again when you are done.'
        : phase === 'thinking'
          ? 'A moment. I still have our last talk.'
          : phase === 'speaking'
            ? 'The new note lands when she finishes.'
            : canShowVoiceNote(phase) || showNow
              ? 'I remember this. Tap when you want to keep going.'
              : firstName
                ? `${firstName}, I am here. Ask about treatment, food, rest, or clinic.`
                : 'Ask about treatment, food, rest, or what to take to clinic.';

  const nowKicker = lang === 'es' ? 'Ahora' : 'Now';
  const earlierKicker = lang === 'es' ? 'Antes' : 'Earlier';
  const backLabel = lang === 'es' ? 'Volver a la app' : 'Back to app';
  const nowPlaceholder = phase === 'speaking'
    ? (lang === 'es' ? 'Estoy diciendo esto ahora.' : 'I am saying this now.')
    : phase === 'listening'
      ? (lang === 'es' ? 'Te estoy escuchando.' : 'I am listening.')
      : (lang === 'es'
        ? 'Cuando termine de hablar, la nota aparece aquí.'
        : 'When she finishes speaking, the note lands here.');

  const returnToApp = () => {
    stopListen(false);
    stopPlayback();
    goProduct(memberPath('/'), navigate);
  };

  return (
    <div className="talk-page" data-testid="talk-page">
      <MetaTags
        title="Talk to Vani"
        description="Tap the circle. Ask out loud. Get a short spoken answer about cancer care. The note appears after she finishes."
        path={talkUrl('/')}
      />
      <header className="talk-bar">
        <div className="talk-bar-start">
          <BrandLogo className="talk-brand" size="md" to={wwwUrl('/')} />
          <button
            type="button"
            className="talk-back"
            onClick={returnToApp}
            aria-label={backLabel}
            title={backLabel}
            data-testid="talk-back-to-app"
          >
            <svg className="talk-back-icon" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path
                d="M10 3.5 5.5 8 10 12.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span className="talk-back-label">{backLabel}</span>
          </button>
        </div>
        <div className="talk-bar-end">
          <div className="talk-langs" role="group" aria-label="Language">
            {LANGS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={lang === item.id ? 'is-on' : ''}
                onClick={() => {
                  setLang(item.id);
                  if (userKey && sessionRef.current) {
                    persistTalk({ language: item.id });
                  }
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
          {isPro ? <span className="talk-plan-chip">PRO</span> : null}
          {isAuthenticated && !isPro ? (
            <button type="button" className="talk-plan-chip is-locked" onClick={() => setPaywall(true)}>
              Plan required
            </button>
          ) : null}
        </div>
      </header>

      <main className="talk-stage">
        <section className="talk-rail talk-rail-past" aria-label="Earlier notes">
          <p className="talk-rail-label">{earlierKicker}</p>
          <div className="talk-rail-scroll" data-testid="talk-notes-past">
            {earlier.length ? earlier.map((item) => (
              <NoteCard key={item.id} kicker={earlierKicker} text={item.text} tone="past" />
            )) : (
              <p className="talk-rail-empty">Earlier notes stay here, beside the one she just said.</p>
            )}
          </div>
        </section>

        <div className="talk-center">
          <div className="talk-glow" aria-hidden="true" />
          <button
            type="button"
            className={`talk-orb is-${phase}`}
            onClick={onOrb}
            disabled={phase === 'thinking' || !entitlementReady}
            aria-label={statusCopy(phase, lang)}
          >
            <TalkRing mode={phase === 'noted' ? 'idle' : phase} energy={energy} />
            <div className="talk-core">
              <p className="talk-status">{statusCopy(phase, lang)}</p>
            </div>
          </button>
          <p className="talk-hint">{hint}</p>
          {alert ? <p className="talk-alert" role="alert">{alert}</p> : null}
        </div>

        <section className="talk-rail talk-rail-now" aria-label="Current note">
          <p className="talk-rail-label">{nowKicker}</p>
          <div className="talk-rail-scroll">
            {showNow ? (
              <NoteCard
                kicker={nowKicker}
                text={liveNote}
                tone="now"
                testId="talk-note"
              />
            ) : (
              <p className="talk-rail-empty">{nowPlaceholder}</p>
            )}
          </div>
        </section>
      </main>

      <footer className="talk-foot">
        <span>A voice turn uses {VOICE_CREDITS} credits · a typed question uses 2 · trained by oncologists</span>
        {!isAuthenticated ? (
          <button type="button" onClick={() => goProduct(wwwUrl('/authentication?redirect=%2Ftalk'), navigate)}>
            Sign in
          </button>
        ) : null}
      </footer>
      {paywall ? (
        <ProPaywallModal
          open
          onClose={() => setPaywall(false)}
          onUnlocked={() => {
            setIsPro(true);
            setPaywall(false);
          }}
          returnPath="/talk"
          title="Voice is on a plan"
          subtitle="Talk to Vani is included with ByOnco PRO. A voice turn uses 6 credits. A typed question uses 2."
        />
      ) : null}
    </div>
  );
}
